import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Seo from "@/components/Seo";

const SEO_META = {
  title: "Nueva contraseña · E.F. Concepción Madrid — Gestión Deportiva",
  description: "Crea una nueva contraseña segura para tu cuenta del panel de gestión deportiva del club E.F. Concepción Madrid. Este enlace caduca a los 30 minutos por seguridad.",
  path: "/reset-password",
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    if (pw !== confirm) { toast.error("Las contraseñas no coinciden"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen grid place-items-center px-4"
           style={{ background: "linear-gradient(135deg,#7E22CE 0%, #4C1D95 100%)" }}>
        <Seo {...SEO_META} noIndex />
        <h1 className="sr-only">Restablecer contraseña</h1>
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardContent className="text-center py-8 space-y-3">
            <p className="text-sm">El enlace de recuperación no es válido.</p>
            <Link to="/login" className="text-sm text-purple-700 font-bold inline-flex items-center gap-1">
              <ArrowLeft className="h-4 w-4" /> Ir al inicio de sesión
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center px-4 py-10"
         style={{ background: "linear-gradient(135deg,#7E22CE 0%, #4C1D95 100%)" }}>
      <Seo {...SEO_META} noIndex />
      <h1 className="sr-only">Restablecer contraseña del panel de gestión</h1>
      <Card className="w-full max-w-md shadow-2xl border-0">
        <CardHeader className="text-center space-y-2">
          <img src="/logo.jpg" alt="E.F. Concepción Madrid"
               className="w-16 h-16 mx-auto rounded-xl object-contain bg-white border border-slate-200" />
          <CardTitle className="text-xl font-black" style={{ fontFamily: "Nunito" }}>
            Nueva contraseña
          </CardTitle>
        </CardHeader>
        <CardContent>
          {done ? (
            <div className="text-center space-y-3" data-testid="reset-success">
              <CheckCircle2 className="h-10 w-10 mx-auto text-emerald-500" />
              <p className="text-sm text-slate-700">
                Tu contraseña se ha actualizado correctamente. Te llevamos al inicio de sesión...
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label htmlFor="pw">Nueva contraseña</Label>
                <Input
                  id="pw"
                  type="password"
                  required
                  minLength={6}
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  placeholder="Mínimo 6 caracteres"
                  data-testid="reset-password"
                />
              </div>
              <div>
                <Label htmlFor="confirm">Repite la contraseña</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold"
                data-testid="reset-submit"
              >
                {loading ? "Guardando..." : "Restablecer contraseña"}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
